
import { useState, useEffect } from 'react';
import { Team, UserProfile } from '../types';
import { TeamService } from '../services/TeamService';
import { UserService } from '../services/UserService';

interface OrganizationTeamsProps {
    userProfile: UserProfile | null | undefined;
    effectiveTeamIds: string[];
}

export const useOrganizationTeams = ({ userProfile, effectiveTeamIds }: OrganizationTeamsProps) => {
    const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
    const [myOrgTeams, setMyOrgTeams] = useState<Team[]>([]);
    const [primaryTeam, setPrimaryTeam] = useState<Team | null>(null);

    // 1. Fetch Team Members (Filtered by orgId)
    useEffect(() => {
        if (!userProfile?.orgId) {
            setAllUsers([]);
            return;
        }
        return UserService.listenToOrgUsers(userProfile.orgId, setAllUsers);
    }, [userProfile?.orgId]);

    // 2. My Organization's Teams Listener
    useEffect(() => {
        if (!userProfile?.orgId) {
            setMyOrgTeams([]);
            return;
        }

        return TeamService.listenToAllOrgTeams(userProfile.orgId, (orgTeams) => {
            // Filter: Only keep teams that the user is actually a member of
            const filtered = orgTeams.filter(team => 
                userProfile.teamIds?.includes(team.id)
            );
            setMyOrgTeams(filtered);
        });
    }, [userProfile?.orgId, JSON.stringify(userProfile?.teamIds)]);

    // 3. Primary Team Info Listener
    useEffect(() => {
        // INSTANT CLEAR: Wipe old team data immediately when Org or selection changes
        setPrimaryTeam(null);

        if (!userProfile?.orgId || effectiveTeamIds.length === 0) {
            return;
        }
        
        const primaryId = effectiveTeamIds[0];
        
        // SECURITY: Before listening, verify that this team actually belongs 
        // to the user's current organization's team list in their profile.
        const isTeamInOrg = userProfile.teams?.some(t => t.teamId === primaryId);
        if (!isTeamInOrg) {
            console.log(`[Sync] Skipping fetch for team ${primaryId} - not in current Org membership.`);
            setPrimaryTeam(null);
            return;
        }

        return TeamService.listenToTeam(primaryId, (data) => {
            if (data) {
                // SECURITY: Double-check orgId match on the actual document
                if (data.orgId === userProfile.orgId) {
                    setPrimaryTeam(data);
                } else {
                    console.warn(`[Sync] OrgID Mismatch for team ${primaryId}. Expected ${userProfile.orgId}, got ${data.orgId}`);
                    setPrimaryTeam(null);
                }
            } else {
                setPrimaryTeam(null);
            }
        });
    }, [userProfile?.orgId, JSON.stringify(effectiveTeamIds)]);

    return {
        allUsers,
        myOrgTeams,
        team: primaryTeam
    };
};
